/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
// the current theme's geofence point colors (no DLS token matches the red) ...
const POINT_COLOR = '#FF0022';
const POINT_FILL  = '#FFFFFF';

// marker sizes are in points (px * 0.75): a 16px marker with a 4px ring
// leaves an 8px center, a midpoint is a 10px ring with a 2px outline and
// the finished point is a solid 12px dot ...
const ACTIVE_POINT_SIZE    = 9;
const ACTIVE_POINT_OUTLINE = 3;
const MIDPOINT_SIZE        = 6;
const MIDPOINT_OUTLINE     = 1.5;
const FINISHED_POINT_SIZE  = 9;

// the Sketch components (Reshape / Box / GraphicMover) already given the
// current-theme styling, so repeated update events do not reset them ...
const styledComponents = new WeakSet<object>();

// how long the click cursor stays visible after a click, so it can be seen ...
const CLICK_CURSOR_DURATION = 250;

// the surface classes of the create states, where a click drops / adds a
// point, and the class the drawing tools set once nothing is being placed ...
const CREATE_STATE_CLASSES = ['crosshair', 'crosshair-only'];
const IDLE_STATE_CLASS     = 'default-cursor';

// the click listener runs in the capture phase, ahead of the ArcGIS
// listeners on the map surface that may stop the event ...
const CLICK_LISTENER_OPTIONS = {capture: true};

// a Box transform handle index is odd for side handles, even for corners ...
const isSideHandle = (box, handle) => (
  box._vertexGraphics.indexOf(handle) % 2 === 1
);

/**
 * Map surface class marking an active geofence drawing or editing session,
 * which scopes the current theme's edit-handle cursors to geofences only.
 */
export const GEOFENCE_DRAWING_CLASS = 'geofence-drawing';

/**
 * Map surface class marking a polygon / freehand shape still being drawn,
 * which selects the current theme's draw-point cursor over drop-location.
 */
export const GEOFENCE_DRAW_SHAPE_CLASS = 'draw-shape';

/**
 * Map surface class present for a moment after a click drops or adds a
 * point, which selects the current theme's click cursor.
 */
export const GEOFENCE_CLICKED_CLASS = 'geofence-clicked';

/**
 * Map surface class for tools whose hoverable graphics are all draggable
 * points (the radial's center / edge), so hovering shows the move cursor
 * instead of the click cursor.
 */
export const GEOFENCE_MOVE_ON_HOVER_CLASS = 'geofence-move-on-hover';

/**
 * Starts a geofence drawing session on the map surface: marks the surface
 * with `GEOFENCE_DRAWING_CLASS` (and `GEOFENCE_MOVE_ON_HOVER_CLASS` when
 * requested), and adds `GEOFENCE_CLICKED_CLASS` for a moment after each
 * click that drops or adds a point. Clicks are read from a document-level
 * capture-phase listener, so ArcGIS tools stopping their own pointer
 * events cannot hide them.
 *
 * ```typescript
 *
 *   import {startGeofenceDrawingSession} from
 *     '@ion/dls/src/maps/geofences/drawingStyles';
 *   ...
 *
 *   useEffect(() => {
 *     const endDrawingSession = startGeofenceDrawingSession(mapViewEl);
 *     return endDrawingSession;
 *   }, []);
 *
 * ```
 *
 * @param mapViewEl     The map view's `.esri-view-surface` element.
 * @param isMoveOnHover `true` when hovering a graphic means it can be moved.
 * @returns             A function ending the session and removing its classes.
 */
export const startGeofenceDrawingSession = (mapViewEl?: Element | null, isMoveOnHover = false) => {
  let clickTimer = null;
  // only scheduled once a surface exists ...
  const onClickEnd = function() {
    mapViewEl.classList.remove(GEOFENCE_CLICKED_CLASS);
  };
  const onPointerDown = function(event: Event) {
    const classList     = mapViewEl?.classList;
    const isCreateState = !classList?.contains(IDLE_STATE_CLASS) && CREATE_STATE_CLASSES.some(function(className) {
      return classList?.contains(className);
    });
    if (isCreateState && mapViewEl.contains(event.target as Node)) {
      clearTimeout(clickTimer);
      classList.add(GEOFENCE_CLICKED_CLASS);
      clickTimer = setTimeout(onClickEnd, CLICK_CURSOR_DURATION);
    }
  };
  mapViewEl?.classList.add(GEOFENCE_DRAWING_CLASS);
  mapViewEl?.classList.toggle(GEOFENCE_MOVE_ON_HOVER_CLASS, isMoveOnHover);
  document.addEventListener('pointerdown', onPointerDown, CLICK_LISTENER_OPTIONS);
  return function() {
    clearTimeout(clickTimer);
    document.removeEventListener('pointerdown', onPointerDown, CLICK_LISTENER_OPTIONS);
    mapViewEl?.classList.remove(GEOFENCE_DRAWING_CLASS, GEOFENCE_CLICKED_CLASS, GEOFENCE_MOVE_ON_HOVER_CLASS);
  };
};

/**
 * Applies the current theme's scaling rules to the Sketch view model's
 * transform Box when a scale starts: dragging a side handle stretches only
 * that side (the opposite side stays put), while dragging a corner scales
 * every side evenly. Call it from the view model's `update` events.
 *
 * ```typescript
 *
 *   import {applyCurrentScaleMode} from
 *     '@ion/dls/src/maps/geofences/drawingStyles';
 *   ...
 *
 *   sketchViewModel.on('update', function(event) {
 *     applyCurrentScaleMode(sketchViewModel, event);
 *   });
 *
 * ```
 *
 * @param sketchViewModel The Sketch view model whose transform Box scales.
 * @param event           The view model's `update` event.
 */
export const applyCurrentScaleMode = (
  sketchViewModel: __esri.SketchViewModel,
  event: __esri.SketchViewModelUpdateEvent
) => {
  const box    = (sketchViewModel as any)?.activeComponent;
  const handle = (event?.toolEventInfo as __esri.UpdateToolEventInfo & {mover?: __esri.Graphic})?.mover;
  if (event?.toolEventInfo?.type === 'scale-start' && box) {
    box.preserveAspectRatio = !isSideHandle(box, handle);
  }
};

/**
 * Builds the current theme's active point symbol - a red ring with a white
 * center - used for vertices being drawn and the radial edge handle.
 *
 * ```typescript
 *
 *   import {getActivePointSymbol} from
 *     '@ion/dls/src/maps/geofences/drawingStyles';
 *   ...
 *
 *   const graphic = new Graphic({geometry, symbol: getActivePointSymbol()});
 *
 * ```
 *
 * @returns A `simple-marker` symbol definition.
 */
export const getActivePointSymbol = () => ({
  type    : 'simple-marker',
  style   : 'circle',
  size    : ACTIVE_POINT_SIZE,
  color   : POINT_FILL,
  outline : {
    color : POINT_COLOR,
    width : ACTIVE_POINT_OUTLINE
  }
});

/**
 * Builds the current theme's active handle symbol - a red square with a
 * white center - used for the transform (scale) box handles.
 *
 * ```typescript
 *
 *   import {getActiveHandleSymbol} from
 *     '@ion/dls/src/maps/geofences/drawingStyles';
 *   ...
 *
 *   graphic.symbol = getActiveHandleSymbol();
 *
 * ```
 *
 * @returns A `simple-marker` symbol definition.
 */
export const getActiveHandleSymbol = () => ({
  ...getActivePointSymbol(),
  style : 'square'
});

/**
 * Builds the current theme's midpoint symbol - a small red ring with a white
 * center - used for the reshape tool's add-a-vertex midpoints.
 *
 * ```typescript
 *
 *   import {getMidpointSymbol} from
 *     '@ion/dls/src/maps/geofences/drawingStyles';
 *   ...
 *
 *   graphic.symbol = getMidpointSymbol();
 *
 * ```
 *
 * @returns A `simple-marker` symbol definition.
 */
export const getMidpointSymbol = () => ({
  ...getActivePointSymbol(),
  size    : MIDPOINT_SIZE,
  outline : {
    color : POINT_COLOR,
    width : MIDPOINT_OUTLINE
  }
});

/**
 * Builds the current theme's finished point symbol - a solid red dot -
 * used for a completed shape's vertices and the radial center.
 *
 * ```typescript
 *
 *   import {getFinishedPointSymbol} from
 *     '@ion/dls/src/maps/geofences/drawingStyles';
 *   ...
 *
 *   const center = new Graphic({geometry, symbol: getFinishedPointSymbol()});
 *
 * ```
 *
 * @returns A `simple-marker` symbol definition.
 */
export const getFinishedPointSymbol = () => ({
  type    : 'simple-marker',
  style   : 'circle',
  size    : FINISHED_POINT_SIZE,
  color   : POINT_COLOR,
  outline : {
    color : POINT_COLOR,
    width : 0
  }
});

// clones the component's own symbol, so the result is an instance of the
// same ArcGIS runtime (bundled or CDN-loaded) the component uses; `type`
// is a read-only symbol property, and setting it throws ...
const cloneSymbol = (base, {type, ...props}) => (
  base.clone().set(props)
);

// turns off the selection halo a Reshape, Box or GraphicMover draws over
// the shape - the setter also removes a halo already drawn ...
const turnOffSelectionHalo = (component) => {
  if (component.highlightsEnabled) {
    component.highlightsEnabled = false;
  }
};

// restyles a Reshape tool's vertices (finished, with the hovered / selected
// one active) and its add-a-vertex midpoints ...
const styleReshapeVertices = (component, {vertices, midpoints}) => {
  const finished    = cloneSymbol(vertices.default, getFinishedPointSymbol());
  const active      = cloneSymbol(vertices.default, getActivePointSymbol());
  const midpoint    = cloneSymbol(midpoints?.default ?? vertices.default, getMidpointSymbol());
  component.symbols = {
    ...component.symbols,
    vertices  : {default: finished, hover: active, selected: active},
    midpoints : {default: midpoint, hover: active}
  };
  component.vertexGraphics?.forEach(function(graphic) {
    graphic.symbol = finished;
  });
  component.midpointGraphics?.forEach(function(graphic) {
    graphic.symbol = midpoint;
  });
};

// restyles a transform Box's square scale handles ...
const styleTransformHandles = (component, {handles}) => {
  const handle      = cloneSymbol(handles.default, getActiveHandleSymbol());
  component.symbols = {
    ...component.symbols,
    handles : {default: handle, hover: handle}
  };
  component.layer?.graphics?.forEach(function(graphic) {
    if (graphic.symbol?.style === 'square') {
      graphic.symbol = handle;
    }
  });
};

/**
 * Applies the current theme's styling to the Sketch view model's active
 * tool component, once per component: turns off the selection halo drawn
 * over the shape (every tool), then restyles a Reshape tool's vertices and
 * midpoints or a transform Box's scale handles. Call it from the view
 * model's `update` events while the current theme is selected.
 *
 * ```typescript
 *
 *   import {applyCurrentToolStyles} from
 *     '@ion/dls/src/maps/geofences/drawingStyles';
 *   ...
 *
 *   sketchViewModel.on('update', function() {
 *     applyCurrentToolStyles(sketchViewModel);
 *   });
 *
 * ```
 *
 * @param sketchViewModel The Sketch view model whose active tool is styled.
 */
export const applyCurrentToolStyles = (sketchViewModel: __esri.SketchViewModel) => {
  // `activeComponent` and its `symbols` are not part of the typed API ...
  const component = (sketchViewModel as any)?.activeComponent;
  if (component && !styledComponents.has(component)) {
    styledComponents.add(component);
    const symbols = component.symbols ?? {};
    turnOffSelectionHalo(component);
    if (symbols.vertices?.default) {
      styleReshapeVertices(component, symbols);
    }
    if (symbols.handles?.default) {
      styleTransformHandles(component, symbols);
    }
  }
};

/**
 * Applies every current-theme drawing rule for a Sketch view model `update`
 * event - the active tool's styling (`applyCurrentToolStyles`) and the
 * transform Box's scaling rules (`applyCurrentScaleMode`). Register it only
 * while the current theme is selected, so the theme is checked once rather
 * than on every pointer move.
 *
 * ```typescript
 *
 *   import {applyCurrentDrawingUpdate} from
 *     '@ion/dls/src/maps/geofences/drawingStyles';
 *   ...
 *
 *   if (isCurrentTheme()) {
 *     sketchViewModel.on('update', function(event) {
 *       applyCurrentDrawingUpdate(sketchViewModel, event);
 *     });
 *   }
 *
 * ```
 *
 * @param sketchViewModel The Sketch view model being updated.
 * @param event           The view model's `update` event.
 */
export const applyCurrentDrawingUpdate = (
  sketchViewModel: __esri.SketchViewModel,
  event: __esri.SketchViewModelUpdateEvent
) => {
  applyCurrentToolStyles(sketchViewModel);
  applyCurrentScaleMode(sketchViewModel, event);
};

/**
 * Registers `applyCurrentDrawingUpdate` on the Sketch view model's `update`
 * events when the current theme is selected (read once by the caller, so
 * the theme is not checked on every pointer move); does nothing otherwise.
 *
 * ```typescript
 *
 *   import {registerCurrentDrawingUpdates} from
 *     '@ion/dls/src/maps/geofences/drawingStyles';
 *   ...
 *
 *   registerCurrentDrawingUpdates(sketchViewModel, isCurrentTheme());
 *
 * ```
 *
 * @param sketchViewModel The Sketch view model to style and scale.
 * @param isCurrent       `true` when the current theme is selected.
 */
export const registerCurrentDrawingUpdates = (sketchViewModel: __esri.SketchViewModel, isCurrent: boolean) => {
  if (isCurrent) {
    sketchViewModel?.on('update', function(event) {
      applyCurrentDrawingUpdate(sketchViewModel, event);
    });
  }
};

/**
 * Determines if a Sketch transform tool graphic is a marker the legacy
 * theme restyles itself - the current theme's handle markers come from
 * `applyCurrentToolStyles` instead.
 *
 * ```typescript
 *
 *   import {isLegacyMarker} from
 *     '@ion/dls/src/maps/geofences/drawingStyles';
 *   ...
 *
 *   if (isLegacyMarker(graphic, isCurrent)) {
 *     graphic.symbol = circleSymbol;
 *   }
 *
 * ```
 *
 * @param   graphic   The transform tool graphic.
 * @param   isCurrent `true` when the current theme is selected.
 * @returns           `true` for a marker under the legacy theme, otherwise
 *                    `false`.
 */
export const isLegacyMarker = (graphic: __esri.Graphic, isCurrent: boolean) => (
  graphic.symbol?.type === 'simple-marker' && !isCurrent
);
